import { useCallback, useEffect, useState } from 'react';

function getProductMinQuantity(product) {
  const direct = Number(product?.minQuantity);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const vendors = product?.vendors;
  if (Array.isArray(vendors) && vendors.length) {
    return Math.max(...vendors.map((v) => Number(v.minQuantity) || 0));
  }
  return 0;
}

function getInventoryStatus(product) {
  return String(product?.inventoryStatus || '').trim().toUpperCase();
}

function mapInventoryItem(product) {
  const quantity = Number(product?.quantity) || 0;
  const minQuantity = getProductMinQuantity(product);
  const shortfall = Math.max(0, minQuantity - quantity);
  return {
    id: product.productId || product.product || product.product_name,
    name: product.product || product.product_name || '—',
    category: product.itemCategory || product.item_category || '',
    quantity,
    minQuantity,
    shortfall,
    orderStatus: product.orderStatus || 'NEW',
    inventoryStatus: getInventoryStatus(product),
  };
}

/** Match store inventory grid: RED/YELLOW status or stock at/below vendor minimum. */
function isInventoryNeeded(product) {
  const quantity = Number(product?.quantity) || 0;
  const minQuantity = getProductMinQuantity(product);
  const status = getInventoryStatus(product);

  if (status === 'RED' || status === 'YELLOW') return true;
  if (minQuantity > 0 && quantity <= minQuantity) return true;
  return false;
}

export function useOperationsData() {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [productionPlans, setProductionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(0);
  const refresh = useCallback(() => setKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_PJCBACKEND_URL;
    if (!base) {
      setInventoryItems([]);
      setProductionPlans([]);
      setLoading(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const [invRes, planRes] = await Promise.allSettled([
          fetch(`${base}/api/product-vendor`),
          fetch(`${base}/api/production-plan`),
        ]);

        if (cancelled) return;

        if (invRes.status === 'fulfilled' && invRes.value.ok) {
          const products = await invRes.value.json();
          const needed = (Array.isArray(products) ? products : [])
            .filter(isInventoryNeeded)
            .map(mapInventoryItem)
            .sort((a, b) => {
              if (b.shortfall !== a.shortfall) return b.shortfall - a.shortfall;
              return String(a.name).localeCompare(String(b.name));
            });
          setInventoryItems(needed);
        } else {
          setInventoryItems([]);
        }

        if (planRes.status === 'fulfilled' && planRes.value.ok) {
          const plans = await planRes.value.json();
          setProductionPlans((Array.isArray(plans) ? plans : []).slice(0, 8).map((p) => ({
            product: p.product_name || p.product || '—',
            target: p.target_quantity ?? p.target ?? '—',
            developed: p.developed ?? p.total_accepted ?? '—',
            dispatched: p.dispatched ?? p.total_dispatched ?? '—',
          })));
        } else {
          setProductionPlans([]);
        }
      } catch {
        if (!cancelled) {
          setInventoryItems([]);
          setProductionPlans([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [key]);

  return { inventoryItems, productionPlans, loading, refresh };
}
